import { useState, useEffect } from "react";
import { ClipboardList, Filter, Loader2, User, Shield, Database, Trash2, UserPlus, LogIn, LogOut, TestTube, Building2 } from "lucide-react";
import { useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getActionIcon = (action) => {
  switch (action) {
    case "LOGIN_SUCCESS":
    case "LOGIN_FAILED":
      return <LogIn className="w-4 h-4" />;
    case "LOGOUT":
      return <LogOut className="w-4 h-4" />;
    case "USER_CREATED":
      return <UserPlus className="w-4 h-4" />;
    case "USER_DELETED":
      return <Trash2 className="w-4 h-4" />;
    case "USER_STATUS_CHANGED":
      return <User className="w-4 h-4" />;
    case "SAP_CONNECTION_CREATED":
    case "SAP_CONNECTION_DELETED":
      return <Database className="w-4 h-4" />;
    case "SAP_CONNECTION_TESTED":
      return <TestTube className="w-4 h-4" />;
    case "TENANT_CREATED":
    case "TENANT_DELETED":
      return <Building2 className="w-4 h-4" />;
    default:
      return <ClipboardList className="w-4 h-4" />;
  }
};

const getActionColor = (action) => {
  if (action.includes("DELETED") || action === "LOGIN_FAILED") {
    return "text-red-600 bg-red-50";
  }
  if (action.includes("CREATED") || action === "LOGIN_SUCCESS") {
    return "text-emerald-600 bg-emerald-50";
  }
  if (action.includes("TESTED")) {
    return "text-blue-600 bg-blue-50";
  }
  return "text-amber-600 bg-amber-50";
};

const formatAction = (action) => {
  return action.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
};

const AuditLog = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState("all");
  
  const fetchLogs = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.append("limit", "50");
      if (actionFilter !== "all") {
        params.append("action", actionFilter);
      }
      
      const response = await axios.get(`${API}/audit-logs?${params.toString()}`);
      setLogs(response.data);
    } catch (error) {
      console.error("Failed to fetch audit logs:", error);
    } finally {
      setLoading(false);
    }
  }, [actionFilter]);
  
  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);
  
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  const uniqueActions = [...new Set(logs.map(l => l.action))];
  
  return (
    <Card className="card-industrial" data-testid="audit-log">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <ClipboardList className="w-5 h-5" />
              Audit Log
            </CardTitle>
            <CardDescription>Track admin actions and system events</CardDescription>
          </div>
          
          <Select value={actionFilter} onValueChange={setActionFilter}>
            <SelectTrigger className="w-[180px]" data-testid="audit-filter">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Filter actions" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Actions</SelectItem>
              <SelectItem value="LOGIN_SUCCESS">Login Success</SelectItem>
              <SelectItem value="LOGIN_FAILED">Login Failed</SelectItem>
              <SelectItem value="USER_CREATED">User Created</SelectItem>
              <SelectItem value="USER_DELETED">User Deleted</SelectItem>
              <SelectItem value="SAP_CONNECTION_CREATED">Connection Created</SelectItem>
              <SelectItem value="SAP_CONNECTION_DELETED">Connection Deleted</SelectItem>
              <SelectItem value="SAP_CONNECTION_TESTED">Connection Tested</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : logs.length === 0 ? (
          <p className="text-center text-muted-foreground py-8">No audit logs found</p>
        ) : (
          <ScrollArea className="h-[400px] pr-4">
            <div className="space-y-3" data-testid="audit-logs-list">
              {logs.map((log) => (
                <div
                  key={log.id}
                  className="flex items-start gap-3 p-3 rounded-md border bg-background hover:bg-secondary/30 transition-colors"
                  data-testid={`audit-log-${log.id}`}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${getActionColor(log.action)}`}>
                    {getActionIcon(log.action)}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className="text-xs">
                        {formatAction(log.action)}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        by <span className="font-medium">{log.user_name}</span>
                      </span>
                    </div>
                    
                    {log.details && Object.keys(log.details).length > 0 && (
                      <p className="text-sm text-muted-foreground mt-1">
                        {Object.entries(log.details).map(([key, value]) => (
                          <span key={key} className="mr-3">
                            <span className="text-xs uppercase tracking-wide">{key.replace(/_/g, " ")}:</span>{" "}
                            <span className="font-medium">{String(value)}</span>
                          </span>
                        ))}
                      </p>
                    )}
                    
                    <p className="text-xs text-muted-foreground mt-1">
                      {formatTimestamp(log.timestamp)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
};

export default AuditLog;
